import { CSSProperties, useCallback, useRef } from "react";
import { SortDescriptor } from "react-aria-components";

import SearchInput from "components/SearchInput";
import {
  EmptystateSearchNoResultsColored,
  EmptystateUsersColored,
} from "components/icons/generated";
import EmptyState from "ds/components/EmptyState";
import Card from "ds/components/Card";
import Box from "ds/components/Box";
import Table from "components/Table";
import TableContextProvider from "components/Table/Context";
import TableRow from "components/Table/Row";
import TableWrapper from "components/Table/Wrapper";
import TableVirtualizedSpacerRow from "components/Table/VirtualizedSpacerRow";

import {
  INITIAL_COLUMNS_VIEW_CONFIG,
  LAUNCHPAD_TRACK_COLUMN_CONFIG,
  LAUNCHPAD_TRACK_STORAGE_KEY,
  LAUNCHPAD_TRACK_FILTERS_ORDER_SETTINGS_KEY,
  ROW_HEADER_ID,
} from "./constants";
import { LaunchPadTrackUserProgress } from "./types";
import LaunchPadTrackItem from "./Item";
import styles from "./styles.module.css";

type LaunchPadTrackListProps = {
  usersWithProgress: LaunchPadTrackUserProgress[];
  loading: boolean;
  searchQuery: string;
  sortDescriptor: SortDescriptor;
  onSortChange: (descriptor: SortDescriptor) => void;
};

const LaunchPadTrackList = ({
  usersWithProgress,
  loading,
  searchQuery,
  sortDescriptor,
  onSortChange,
}: LaunchPadTrackListProps) => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);

  const renderRow = useCallback(
    (
      item: Partial<LaunchPadTrackUserProgress> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return <TableVirtualizedSpacerRow height={item.height} />;
      }

      if (!item.userLogin) return null;

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.userLogin}
        >
          <LaunchPadTrackItem user={item as LaunchPadTrackUserProgress} />
        </TableRow>
      );
    },
    []
  );

  return (
    <Card
      contentClassName={styles.cardContent}
      fullWidth
      fullbleed
      title="Team progress"
      zeroMinWidth
      zeroMinHeight
      grow="1"
      overflowHidden
      headerSuffix={
        <SearchInput
          placeholder="Search by username..."
          filtersOrderSettingsKey={LAUNCHPAD_TRACK_FILTERS_ORDER_SETTINGS_KEY}
        />
      }
    >
      {searchQuery && !usersWithProgress.length && !loading && (
        <Box grow="1" align="center" justify="center">
          <EmptyState
            icon={EmptystateSearchNoResultsColored}
            title="No results"
            caption="Check the spelling or try something less specific."
          />
        </Box>
      )}

      {!loading && !usersWithProgress.length && !searchQuery && (
        <Box grow="1" align="center" justify="center">
          <EmptyState
            icon={EmptystateUsersColored}
            title="No managed users found"
            caption="There are no managed users in your organization yet."
          />
        </Box>
      )}

      {(!!usersWithProgress.length || loading) && (
        <TableContextProvider
          columnsConfig={LAUNCHPAD_TRACK_COLUMN_CONFIG}
          initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
          rowHeaderId={ROW_HEADER_ID}
          localStorageId={LAUNCHPAD_TRACK_STORAGE_KEY}
          sortDescriptor={sortDescriptor}
          setSortDescriptor={onSortChange}
        >
          <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef} fullPage={false}>
            <Table
              ariaLabel="Track managed users table"
              items={usersWithProgress}
              ref={virtualizedListContainerRef}
              loadingContent={loading && !usersWithProgress.length}
            >
              {renderRow}
            </Table>
          </TableWrapper>
        </TableContextProvider>
      )}
    </Card>
  );
};

export default LaunchPadTrackList;
